import config from 'constants/config'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import productLabels from 'i18n/productLabels'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { capitalise } from 'lib/string/stringUtils'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

/** Shared with designation labels in `checkout/utils` so EN/DE stay in sync. */
export const accommodationDesignationMessages = defineMessages({
  accommodation: {
    id: 'zdmuwpJMsikmxWcwof6n',
    defaultMessage: 'accommodation',
  },
  preAccommodation: {
    id: 'NnZL1OXra6V4ITrsVqOF',
    defaultMessage: 'pre accommodation',
  },
  postAccommodation: {
    id: 's2dGUEKVliNUQz3K9hQn',
    defaultMessage: 'post accommodation',
  },
})

/**
 * Display messages for known checkout price-breakdown section titles.
 * Producers set `{ kind: 'known', key }`; only the UI calls `resolvePriceBreakdownTitle`.
 */
export const priceBreakdownTitleMessages = defineMessages({
  accommodation: {
    id: '5YTw3yvipPddoj1taqU6',
    defaultMessage: 'Accommodation',
  },
  hotelPlusFlightsPackage: {
    id: 'yce-MyTLUF_tXbR7VvEg',
    defaultMessage: 'Hotel + Flights Package',
  },
  optionalExperiences: {
    id: 'UUZ27jW_FPEmtCN490UQ',
    defaultMessage: 'Optional experiences',
  },
  transfers: {
    id: 'KAJt4877eOhLAGk60KY5',
    defaultMessage: 'Transfers',
  },
  giftCard: {
    id: 'meLG8DcbHewQZq9s6nav',
    defaultMessage: 'Gift Card',
  },
  purchaseCredit: {
    id: 'NdWpBQNgt0VVYkeNYIxm',
    defaultMessage: 'Purchase Credit',
  },
  travelEsim: {
    id: 'BT6gFIM2VjNiDHJUTt93',
    defaultMessage: 'Travel eSIM',
  },
  additionalDataRoaming: {
    id: 'VvoeeuxWV-e2k_0PA7RL',
    defaultMessage: 'Additional Data Roaming',
  },
  airportLoungePass: {
    id: 'V-0Kx6DXhHrcvnLImsJZ',
    defaultMessage: 'Airport lounge pass',
  },
  luxPlusMembership: {
    id: '7ClYlwOBXpLIELEWdLdZ',
    defaultMessage: '{programName} Membership',
  },
  travelInsurance: {
    id: 'ObqrvteA_97C9Pe3iu3T',
    defaultMessage: 'Travel {insuranceProductName}',
  },
  cancellationInsurance: {
    id: 'zAVIfqIX9uPiHBW4t_W8',
    defaultMessage: 'Cancellation {insuranceProductName}',
  },
  travelInsuranceFixed: {
    id: 'mF2rZmNfE-irHifMnZ1o',
    defaultMessage: 'Travel Insurance',
  },
  pointsToPurchase: {
    id: '_lsD5QgEijPJpp50pjgW',
    defaultMessage: 'Points to purchase',
  },
  flightClassUpgrade: {
    id: 'XsIxMeeHcllZiON4Mudg',
    defaultMessage: 'Flight class upgrade',
  },
  airlineChangeFee: {
    id: 'wX_Ni_5p2LEqzAftTCIS',
    defaultMessage: 'Airline change fee',
  },
  brandChangeFee: {
    id: 'CVMEccdqXijncKzMXivH',
    defaultMessage: '{brandName} change fee',
  },
  newFlightAdditionalCost: {
    id: 'Fep10ixzHW-mF9zRMMnz',
    defaultMessage: 'New flight additional cost',
  },
  package: {
    id: 'tnkE1tpiUk0lj-AQWi7j',
    defaultMessage: 'Package',
  },
  carHireInsurance: {
    id: 'tOzW8VtMLXqn26Ehrwky',
    defaultMessage: 'Insurance',
  },
  carHireAddons: {
    id: '1zYNmZLXaJBPN6v6EuVS',
    defaultMessage: 'Add-ons',
  },
  additionalCharges: {
    id: '0DSJ6BDaG7bj7xtxUnvi',
    defaultMessage: 'Additional charges',
  },
  hotelPlusCoPayment: {
    id: '4n2vqP65XzDKjKxRGr9v',
    defaultMessage: 'Hotel + Co-Payment',
  },
})

const DESIGNATION_TITLE_KEYS = new Set<App.Checkout.PriceBreakdownTitleKey>([
  'preAccommodation',
  'postAccommodation',
])

const KNOWN_TITLE_DESCRIPTORS: Record<App.Checkout.PriceBreakdownTitleKey, MessageDescriptor> = {
  accommodation: priceBreakdownTitleMessages.accommodation,
  preAccommodation: accommodationDesignationMessages.preAccommodation,
  postAccommodation: accommodationDesignationMessages.postAccommodation,
  hotelPlusFlightsPackage: priceBreakdownTitleMessages.hotelPlusFlightsPackage,
  experiences: productLabels.experiences,
  optionalExperiences: priceBreakdownTitleMessages.optionalExperiences,
  transfers: priceBreakdownTitleMessages.transfers,
  giftCard: priceBreakdownTitleMessages.giftCard,
  purchaseCredit: priceBreakdownTitleMessages.purchaseCredit,
  travelEsim: priceBreakdownTitleMessages.travelEsim,
  additionalDataRoaming: priceBreakdownTitleMessages.additionalDataRoaming,
  airportLoungePass: priceBreakdownTitleMessages.airportLoungePass,
  luxPlusMembership: priceBreakdownTitleMessages.luxPlusMembership,
  cancellationProtection: bookingProtectionGlossaryMessages.cancellationProtection,
  luxuryEscapesProtect: bookingProtectionGlossaryMessages.luxuryEscapesProtect,
  travelProtection: bookingProtectionGlossaryMessages.travelProtection,
  travelInsurance: priceBreakdownTitleMessages.travelInsurance,
  cancellationInsurance: priceBreakdownTitleMessages.cancellationInsurance,
  travelInsuranceFixed: priceBreakdownTitleMessages.travelInsuranceFixed,
  pointsToPurchase: priceBreakdownTitleMessages.pointsToPurchase,
  flightClassUpgrade: priceBreakdownTitleMessages.flightClassUpgrade,
  airlineChangeFee: priceBreakdownTitleMessages.airlineChangeFee,
  brandChangeFee: priceBreakdownTitleMessages.brandChangeFee,
  newFlightAdditionalCost: priceBreakdownTitleMessages.newFlightAdditionalCost,
  tour: productLabels.tour,
  cruise: productLabels.cruise,
  package: priceBreakdownTitleMessages.package,
  carHireInsurance: priceBreakdownTitleMessages.carHireInsurance,
  carHireAddons: priceBreakdownTitleMessages.carHireAddons,
  additionalCharges: priceBreakdownTitleMessages.additionalCharges,
}

function getDefaultTitleValues(
  key: App.Checkout.PriceBreakdownTitleKey,
): Record<string, string | number> | undefined {
  switch (key) {
    case 'luxPlusMembership':
      return { programName: LUXURY_PLUS.PROGRAM_NAME }
    case 'brandChangeFee':
      return { brandName: config.brandConfig.name }
    default:
      return undefined
  }
}

export function knownPriceBreakdownTitle(
  key: App.Checkout.PriceBreakdownTitleKey,
  values?: Record<string, string | number>,
): App.Checkout.PriceBreakdownTitle {
  return values ? { kind: 'known', key, values } : { kind: 'known', key }
}

export function customPriceBreakdownTitle(text: string): App.Checkout.PriceBreakdownTitle {
  return { kind: 'custom', text }
}

export function resolvePriceBreakdownTitle(
  title: App.Checkout.PriceBreakdownTitle,
  intl: IntlShape,
): string {
  if (title.kind === 'custom') {
    return title.text
  }

  const resolved = intl.formatMessage(
    KNOWN_TITLE_DESCRIPTORS[title.key],
    { ...getDefaultTitleValues(title.key), ...title.values },
  )

  // Designation labels are lowercase in EN; section headings capitalise the first letter.
  return DESIGNATION_TITLE_KEYS.has(title.key) ? capitalise(resolved) : resolved
}
